import { useEffect, useRef, useState } from 'react';

import { type MessageStep } from '@/util/interfaces';

/** How long a step stays on screen as running, however fast it finishes. */
export const MINIMUM_ACTIVE_MS = 2000;

interface HeldSteps {
  /** The steps to render, with anything still inside its hold shown as active. */
  steps: MessageStep[];
  /**
   * Whether a step is finished underneath but still being shown as running.
   * The wait has to stay on screen while this is true, otherwise the answer
   * replaces the step list and the hold has nothing left to hold.
   */
  holding: boolean;
}

/**
 * Keeps a step showing as running for {@link MINIMUM_ACTIVE_MS}, however fast
 * the backend finishes it. A spinner that appears and vanishes within a frame
 * reads as a flicker rather than as work, and the steps that finish instantly
 * are precisely the ones whose labels the user never gets to read. The last
 * step is the one this matters most for: the backend closes it in the same
 * breath as the first chunk of the answer.
 *
 * Only steps the caller saw start are held. A message replayed from history
 * arrives with every step already done, so nothing is held and nothing spins -
 * which also means a stored message left with a stuck active step cannot pin
 * the wait open, since `holding` only ever reflects a hold this hook started.
 */
export const useMinimumActiveTime = (steps: MessageStep[]): HeldSteps => {
  const startedAt = useRef(new Map<string, number>());
  const [, forceRender] = useState(0);

  const now = Date.now();
  for (const step of steps) {
    if (step.status === 'active' && !startedAt.current.has(step.key)) {
      startedAt.current.set(step.key, now);
    }
  }

  const heldUntil = (step: MessageStep) => {
    const started = startedAt.current.get(step.key);
    return step.status === 'done' && started !== undefined ? started + MINIMUM_ACTIVE_MS : 0;
  };

  const held = steps.map((step) =>
    // The detail goes with the done state: holding one back while showing the
    // other would put "Reviewed 5 findings" next to a spinner.
    now < heldUntil(step) ? { ...step, status: 'active' as const, detail: undefined } : step,
  );

  const nextRelease = Math.min(...steps.map(heldUntil).filter((releaseAt) => releaseAt > now));
  const holding = Number.isFinite(nextRelease);

  useEffect(() => {
    if (!holding) return;

    const timer = setTimeout(() => forceRender((tick) => tick + 1), nextRelease - Date.now());
    return () => clearTimeout(timer);
  }, [holding, nextRelease]);

  return { steps: held, holding };
};
